// Single design-token source for the Manual Reports module.
// EVERY value below is referenced by name in the page/component files —
// no raw color or pixel value should appear in JSX outside of this file.
//
// Tokens follow a calm, restrained palette: one accent, a neutral scale,
// one success/danger. Spacing is on a 4px grid (4/8/12/16/20/24/32/40).
// Typography uses a 4-step scale with deliberate line-height for breathing room.

export const tokens = {
  color: {
    // Neutrals (the bones of the UI)
    bg:           '#f7f8fa',     // page background
    surface:      '#ffffff',     // cards, inputs
    surfaceAlt:   '#fafbfc',     // subtle stripes / nested surfaces
    border:       '#e6e8eb',     // hairlines
    borderStrong: '#d4d7dc',     // input borders when focused/hovered
    divider:      '#eef0f3',

    // Text
    textStrong:   '#0f172a',     // headings
    text:         '#1f2937',     // body
    textMuted:    '#64748b',     // secondary
    textFaint:    '#94a3b8',     // labels, captions
    textOnAccent: '#ffffff',

    // Accent — ONE primary, plus state colors
    accent:       '#2563eb',
    accentBg:     '#eff6ff',
    accentBorder: '#bfdbfe',

    success:      '#16a34a',
    successBg:    '#f0fdf4',
    successBorder:'#bbf7d0',

    warning:      '#d97706',
    warningBg:    '#fffbeb',

    danger:       '#dc2626',
    dangerBg:     '#fef2f2',

    // Role chips
    roleBd:       '#0ea5e9',
    roleBdBg:     '#f0f9ff',
    roleData:     '#8b5cf6',
    roleDataBg:   '#f5f3ff',
    roleEmail:    '#f59e0b',
    roleEmailBg:  '#fffbeb',
  },

  // Category palette — each activity category gets its own accent so the
  // form has visual rhythm. Used on category labels + matching totals tile.
  category: {
    leads:    { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
    linkedin: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    emails:   { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    calls:    { color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8' },
    data:     { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
    work:     { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },  // data team's "Daily Work"
    dataQuality: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' }, // email ops
    other:    { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  },

  // 4-px spacing grid. Use these named keys — never raw px.
  space: {
    xxs: 4,
    xs:  8,
    sm:  12,
    md:  16,
    lg:  20,
    xl:  24,
    xxl: 32,
    xxxl:40,
    xxxxl:48,
  },

  // Type scale — 4 steps. Line-heights are generous (1.5-1.6) for readability.
  type: {
    pageTitle: { fontSize: 24, fontWeight: 700, lineHeight: 1.3, letterSpacing: -0.3 },
    sectionTitle: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 },
    label:     { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' },
    body:      { fontSize: 13, fontWeight: 400, lineHeight: 1.55 },
    bodyStrong:{ fontSize: 13, fontWeight: 600, lineHeight: 1.55 },
    caption:   { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
    mono:      { fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontVariantNumeric: 'tabular-nums' },
    stat:      { fontSize: 28, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 },
    statSmall: { fontSize: 18, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  },

  radius: {
    sm: 6,
    md: 10,
    lg: 14,
    pill: 999,
  },

  // ONE shadow level. Borders do the rest.
  shadow: {
    card: '0 1px 2px rgba(15, 23, 42, 0.04)',
    raised: '0 4px 12px rgba(15, 23, 42, 0.08)',
  },

  duration: {
    fast: '120ms',
    base: '180ms',
  },
};

// ─── styled component primitives ─────────────────────────────────────────
// Style objects only — no JSX. Imported and spread into the right antd
// component or div in each page.

export const styles = {
  page: {
    background: tokens.color.bg,
    minHeight: '100vh',
    padding: `${tokens.space.xl}px ${tokens.space.xl}px ${tokens.space.xxxl}px`,
    boxSizing: 'border-box',
    width: '100%',
  },
  pageInner: {
    // Fills the available width — the dashboard's main area already provides
    // sidebar/tab insets, so adding another maxWidth here just leaves dead space.
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space.lg,
    boxSizing: 'border-box',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.space.lg,
    flexWrap: 'wrap',
  },
  pageTitle: {
    ...tokens.type.pageTitle,
    color: tokens.color.textStrong,
    margin: 0,
  },
  pageSubtitle: {
    ...tokens.type.body,
    color: tokens.color.textMuted,
    marginTop: tokens.space.xs,
  },

  card: {
    background: tokens.color.surface,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    padding: tokens.space.xl,
    // Defensive: prevent any global element style from changing this into flex.
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.space.md,
    marginBottom: tokens.space.lg,
  },
  cardTitle: {
    ...tokens.type.sectionTitle,
    color: tokens.color.textStrong,
    margin: 0,
  },
  cardLabel: {
    ...tokens.type.label,
    color: tokens.color.textFaint,
    margin: 0,
  },

  // Field-style row: label on the left, control on the right
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: tokens.space.md,
    paddingBlock: tokens.space.sm,
    minHeight: 40,
  },
  fieldLabel: {
    ...tokens.type.body,
    color: tokens.color.text,
  },

  // Generic input styling overrides — use as `style` on antd Input/InputNumber
  input: {
    height: 40,
    borderRadius: tokens.radius.md,
  },

  // Stat tile
  statTile: {
    background: tokens.color.surface,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    padding: `${tokens.space.lg}px ${tokens.space.xl}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space.xs,
    minHeight: 96,
  },
  statTileLabel: {
    ...tokens.type.label,
    color: tokens.color.textFaint,
  },
  statTileValue: {
    ...tokens.type.stat,
    color: tokens.color.textStrong,
  },
  statTileSub: {
    ...tokens.type.caption,
    color: tokens.color.textMuted,
  },

  // Role chip
  roleChip: (color, bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space.xxs,
    padding: '4px 10px',
    borderRadius: tokens.radius.pill,
    background: bg,
    color,
    ...tokens.type.label,
    fontSize: 10.5,
  }),
};

// Helper: get role chip colors for one of bd/data/email.
export const roleChipColors = (roleKey) => {
  if (roleKey === 'bd')    return { color: tokens.color.roleBd,    bg: tokens.color.roleBdBg };
  if (roleKey === 'data')  return { color: tokens.color.roleData,  bg: tokens.color.roleDataBg };
  if (roleKey === 'email') return { color: tokens.color.roleEmail, bg: tokens.color.roleEmailBg };
  return { color: tokens.color.textMuted, bg: tokens.color.surfaceAlt };
};

// Helper: get KPI accent for one of leads/linkedin/emails/calls/data/total.
// All KPIs use the same accent — restraint. The number itself carries the meaning.
export const kpiAccent = () => tokens.color.accent;

// Helper: get category accent triple. Falls back to neutral if unknown.
export const categoryAccent = (categoryKey) =>
  tokens.category[categoryKey] || tokens.category.other;

// Helper: subtotal key → matching category accent (for the totals strip).
// New unified subtotals: totalLeads / totalLinkedIn / totalEmails / totalCalls / totalData / totalOther
export const subtotalAccent = (subtotalKey) => {
  if (!subtotalKey) return tokens.category.other;
  const k = subtotalKey.toLowerCase();
  if (k.includes('linkedin')) return tokens.category.linkedin;
  if (k.includes('email'))    return tokens.category.emails;
  if (k.includes('call'))     return tokens.category.calls;
  if (k.includes('lead'))     return tokens.category.leads;
  if (k.includes('data'))     return tokens.category.data;
  return tokens.category.other;
};
